import { Directive, ViewChild } from "@angular/core";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { formatCurrency, formatCurrencyShorthand, formatNumber, formatNumberShorthand } from "@ponder-shared/utils/numberFormatter";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";
import { ChartData, ChartOptions, Tick } from "chart.js";
import { BaseChartDirective } from "ng2-charts";

export enum ChartDataLabelType {
    Text,
    Number,
    Currency
};

@Directive()
export abstract class ChartBaseComponent  {
    @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
    
    public chartOptions: ChartOptions = {}
    public chartData: ChartData = { labels: [], datasets: [] }

    public dataLabel: string = "Data";
    public dataLabelType: ChartDataLabelType = ChartDataLabelType.Number;
    public isResponsive: boolean = true;
    public isLegendVisible: boolean = false;
    public maintainAspectRatio: boolean = true;
    public largeScreenAspectRatio = 2;
    public smallScreenAspectRatio = 1.25;
    public lineColor: string = "#AA00AA";
    public lineWidth: number = 1;
    public lineRadius: number = 1;
    public lineBorderWidth: number = 1;
    public lineBorderRadius: number = 1;
    public xGridLineVisible: boolean = true;
    public xGridLineColor?: string;
    public yGridLineVisible: boolean = true;
    public yGridLineColor?: string;
    public xLabelColor?: string;
    public yLabelColor?: string;
    public xGridTickVisible: boolean = true;
    public xGridBorderVisible: boolean = true;
    public yGridTickVisible: boolean = true;
    public yGridBorderVisible: boolean = true;
    public yTicksOnLeft: boolean = true;
    public fillArea: boolean = false;
    public backgroundColor?: string;                // If set, then can not do per-item coloring like in pie charts.
    public autoscale: boolean = true;
    
    protected minYValue: number = 0;
    protected maxYValue: number = 0;

    public constructor(protected events: EventBroker) {

    }
    
    public ngOnInit(): void {
        this.resetData();
        this.rebuildOptions();
    }

    protected resetData(): void {
        this.chartData = {
            labels: [],
            datasets: [{
                label: this.dataLabel,
                data: [],
                backgroundColor: this.backgroundColor ?? [],
                borderColor: this.lineColor,
                pointBorderColor: this.lineColor,
                pointBackgroundColor: this.lineColor,
                borderWidth: this.lineBorderWidth,
                borderRadius: this.lineBorderRadius,
                fill: this.fillArea
            }]
        };
    }

    protected rebuildOptions(): void {
        this.chartOptions = {
            responsive: this.isResponsive,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            maintainAspectRatio: this.maintainAspectRatio,
            aspectRatio: isMobileScreen() ? this.smallScreenAspectRatio : this.largeScreenAspectRatio,
            scales: {
                x: {
                    grid: {
                        display: this.xGridLineVisible,
                        color: this.xGridLineColor
                    },
                    ticks: {
                        display: this.xGridTickVisible,
                        color: this.xLabelColor
                    },
                    border: {
                        display: this.xGridBorderVisible
                    },
                },
                y: {
                    type: "linear",
                    position: (this.yTicksOnLeft ? "left" : "right"),
                    grid: {
                        display: this.yGridLineVisible,
                        color: this.yGridLineColor
                    },
                    ticks: {
                        display: this.yGridTickVisible,
                        color: this.yLabelColor
                    },
                    border: {
                        display: this.yGridBorderVisible
                    }
                }
            },
            plugins: {
                legend: {
                    display: this.isLegendVisible
                },
                tooltip: { callbacks: { } }
            }
        };

        switch (this.dataLabelType) {
            case ChartDataLabelType.Number: {
                this.chartOptions.scales!["y"]!.ticks!.callback = (label: number | string, index: number, ticks: Tick[]) => {
                    return formatNumberShorthand(label as number);
                }
    
                this.chartOptions.plugins!.tooltip!.callbacks!.label = (context) => {
                    let label = context.dataset.label || '';
                    return label + ": " + formatNumber(context.parsed.y);
                }
                break;
            }

            case ChartDataLabelType.Currency: {
                this.chartOptions.scales!["y"]!.ticks!.callback = (label: number | string, index: number, ticks: Tick[]) => {
                    return formatCurrencyShorthand(label as number);
                }
    
                this.chartOptions.plugins!.tooltip!.callbacks!.label = (context) => {
                    let label = context.dataset.label || '';
                    return label + ": " + formatCurrency(context.parsed.y);
                }
                break;
            }

            default:
                break;
        }
    }

    public onToggleAutoscale(): void {
        this.autoscale = !this.autoscale;
        this.setSuggestedYScale();
        this.redrawChart();
    }

    protected redrawChart(): void {
        this.chart.update("default");
        this.chart.render();
    }

    protected addDataPoint(value: number | null, label?: string, color?: string): void {
        this.chartData.datasets![0].data!.push(value);

        if (label) {
            this.chartData.labels!.push(label);
        }

        if (color) {
            (this.chartData.datasets![0].backgroundColor! as string[]).push(color);
        }
    }

    protected setSuggestedYScale(): void {
        this.chartOptions.scales!["y"]!.suggestedMin = (this.autoscale ? this.minYValue * 0.95 : 0);
        this.chartOptions.scales!["y"]!.suggestedMax = (this.maxYValue * 1.05);
    }

    protected setLabels(labels: string[]): void {
        this.chartData.labels = labels;
    }
}